import { type Moment } from "moment";

import type { Int32BTServiceGroupedListItem } from "@btnet/services-btnet/handlers/models/int32BTServiceGroupedListItem";

import { type UserInfo } from "helpers/AppProvider.types";

import { BTLoginTypes, PdfSignatureRequestVersion } from "types/enum";

import { formattedPlural } from "utilities/string/string";

import {
    type ISignatureAssignedUser,
    type UserTypeIcon,
} from "commonComponents/entity/media/signatures/common/signatures.type";

export const getIconNameForUserType = (userType: BTLoginTypes | undefined): UserTypeIcon => {
    switch (userType) {
        case BTLoginTypes.BUILDER:
            return "InternalUsers";
        case BTLoginTypes.SUBS:
            return "SubsVendors";
        case BTLoginTypes.CONTACT:
            return "Homeowners";
        default:
            return null;
    }
};

/**
 * True when a signature request predates the Nutrient signer.
 *
 * The server-side `canSign` permission is version-blind — `MediaListEngine.GetSigningPermissions`
 * grants it on "assigned and pending" alone — so every surface that offers signing has to exclude
 * V1 itself, or a legacy request is handed to the V2 signer with data it cannot read.
 *
 * @param signatureRequestVersion - The request's version, if the entity carries one
 */
export const isLegacySignatureRequest = (
    signatureRequestVersion: PdfSignatureRequestVersion | undefined,
): boolean => signatureRequestVersion === PdfSignatureRequestVersion.V1;

export const SignatureLineLabel = "Signature";
export const NameLineLabel = "Name";
export const DateLineLabel = "Date";

// Telling a real signature block apart from a sentence that happens to contain "Signature", "Name"
// and "Date" - which is what once put the signature widget in the middle of a PO description.

/** Every label the signature block prints, so a gap between two of them can allow the third. */
const signatureBlockLabels = [SignatureLineLabel, NameLineLabel, DateLineLabel];

/** The vertical extent of a search hit or text line, in PDF points. */
export interface ISignatureVerticalSpan {
    top: number;
    height: number;
}

/** A text-layer line, as returned by `textLinesForPageIndex`. */
export interface ISignatureTextLine extends ISignatureVerticalSpan {
    pageIndex: number | null;
    contents: string;
}

/**
 * Whether the text between two labels is blank - ignoring the block's other labels.
 *
 * A signature block prints "Signature    Name    Date", so the gap between "Signature" and
 * "Date" holds "Name". That still counts as blank. A sentence between them does not.
 *
 * Returns `null`, not `false`, when it cannot tell - a label is missing, or there is no gap between
 * them to read. Callers must treat that as "no opinion" and fall back to alignment, so an unreadable
 * text layer never costs us a field.
 */
export const isSeparatedByWhitespaceOnly = (
    lineContents: string,
    firstLabel: string,
    secondLabel: string,
): boolean | null => {
    const firstIndex = lineContents.indexOf(firstLabel);
    const secondIndex = lineContents.indexOf(secondLabel);

    if (firstIndex === -1 || secondIndex === -1) {
        return null;
    }

    const [earlierIndex, earlierLabel, laterIndex] =
        firstIndex <= secondIndex
            ? ([firstIndex, firstLabel, secondIndex] as const)
            : ([secondIndex, secondLabel, firstIndex] as const);

    const gapStart = earlierIndex + earlierLabel.length;
    if (gapStart >= laterIndex) {
        // No gap to read. Either one label sits inside the other, as "Name" does in "Company Name",
        // or they are touching - and an empty gap is not evidence of a signature block.
        return null;
    }

    // Blank out any of the block's own labels sitting in the gap. On a three-column block, "Name"
    // sits between "Signature" and "Date", and that is still a real signature block.
    const gap = signatureBlockLabels.reduce(
        (text, label) => text.split(label).join(" "),
        lineContents.slice(gapStart, laterIndex),
    );

    // Whatever is left must be blank. Any real word means these labels came from a sentence.
    return /^[\s:]*$/.test(gap);
};

/** Whether two vertical spans overlap at all, so a hit can be tied to the line it sits on. */
const verticallyOverlaps = (a: ISignatureVerticalSpan, b: ISignatureVerticalSpan): boolean =>
    a.top < b.top + b.height && b.top < a.top + a.height;

/**
 * Finds the line both labels sit on, if they share one.
 *
 * Matches on vertical position, not just the page number - a page can hold one block per
 * signer, and we need each signer's own line.
 *
 * Returns `undefined` if each label landed on its own line, which some PDFs do. That is fine;
 * alignment decides instead.
 */
export const findSharedTextLine = <T extends ISignatureTextLine>(
    textLines: readonly T[],
    hit: ISignatureVerticalSpan & { pageIndex: number },
    firstLabel: string,
    secondLabel: string,
): T | undefined =>
    textLines.find(
        (line) =>
            line.pageIndex === hit.pageIndex &&
            verticallyOverlaps(line, hit) &&
            line.contents.includes(firstLabel) &&
            line.contents.includes(secondLabel),
    );

export const getFriendlyDueDate = (dueDate?: Moment) => {
    if (!dueDate) {
        return "";
    }

    return dueDate.format("MMMM D, YYYY [at] h:mmA");
};

export const getFriendlyReminder = (reminder?: number | null) => {
    if (!reminder) {
        return "";
    }

    return `${reminder} ${formattedPlural({
        one: "day",
        other: "days",
        value: reminder,
    })} before due date`;
};

export const signeeColors = [
    "#FFD49A",
    "#D1D6FF",
    "#FFCDC1",
    "#FAC2F9",
    "#BCE4FF",
    "#F8DC83",
    "#D3DBE2",
    "#E4CEFD",
    "#AFF2D7",
    "#88FFFF",
];

export const getSigneeColor = (colorsUsed: string[]): string => {
    // Find the first color not in the colorsUsed array
    for (const color of signeeColors) {
        if (!colorsUsed.includes(color)) {
            return color;
        }
    }

    // If all colors are used, start reusing colors from the beginning
    // Use modulo to cycle through colors based on how many are already used
    const colorIndex = colorsUsed.length % signeeColors.length;
    return signeeColors[colorIndex];
};

const createSignatureAssignedUser = (user: UserInfo): ISignatureAssignedUser | null => {
    if (!user.globalUserId || !user.fullName) {
        return null;
    }

    return {
        id: user.globalUserId,
        name: user.fullName,
        loginType: user.loginType,
    };
};

/**
 * Returns the assigned user for a signature request, accounting for users with multi-builder access.
 *
 * @param assignedToList Current list of possible assigned users
 * @param currentUser Current user in context (user who is attempting to sign)
 * @param isExternalUser Is the current user an external user
 *
 * @returns ISignatureAssignedUser with id, name, and loginType when a valid user is found.
 *          Returns null otherwise
 */
export const getAssignedToSignee = (
    assignedToList: Int32BTServiceGroupedListItem[],
    currentUser: UserInfo | undefined,
    isExternalUser: boolean,
): ISignatureAssignedUser | null => {
    if (isExternalUser || !currentUser || assignedToList.length === 0) return null;

    const flattenSelectedAssignedTo =
        assignedToList
            .flatMap((user) => user.options)
            .filter((flattenedUser) => flattenedUser?.selected) ?? [];

    // With multi-builder users, currentUser.globalUserId may not match the assigned user's id.
    const assignedUser = flattenSelectedAssignedTo.find((user) =>
        currentUser.orgLinks?.some((link) => link.orgLinkId === user?.id),
    );

    // Allow the user to sign if they are assigned
    if (assignedUser) {
        return createSignatureAssignedUser({
            globalUserId: assignedUser.id,
            fullName: assignedUser.name,
            loginType: currentUser.loginType,
        } as UserInfo);
    }

    // Allow internal users to manually approve and unassigned users to approve
    return createSignatureAssignedUser(currentUser);
};
